
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { RootState } from '@/store';
import { useFetchUserDataQuery } from '@/store/apis/userApi';
import { isPublicPath } from '@/routes/publicPaths';
import { logger } from '@/lib/logger';

const AuthListener = () => {
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const prevAuthRef = useRef<boolean>(isAuthenticated);

  // Source the user id (not email/PII) for telemetry correlation. `me` is
  // already fetched by several pages; RTK Query dedupes the request.
  const { data: meResponse } = useFetchUserDataQuery(undefined, {
    skip: !isAuthenticated,
  });
  const userId: string | number | undefined = meResponse?.data?.id;

  useEffect(() => {
    // Only bounce to login when a session is lost on a page that actually
    // requires auth. On public pages (e.g. /blueprint) a stale token being
    // cleared must not redirect the visitor — see isPublicPath.
    if (
      prevAuthRef.current &&
      !isAuthenticated &&
      !isPublicPath(location.pathname)
    ) {
      message.info('Your session has expired. Please log in again.');
      navigate('/auth');
    }
    prevAuthRef.current = isAuthenticated;
  }, [isAuthenticated, navigate, location.pathname]);

  // Correlate telemetry to a user id on login/session-restore; drop it on logout.
  useEffect(() => {
    if (isAuthenticated && userId != null) {
      logger.setUser(userId);
    } else if (!isAuthenticated) {
      logger.clearUser();
    }
  }, [isAuthenticated, userId]);

  return null; // This component doesn't render anything
};

export default AuthListener;
